import { api } from "./axiosClient";

// Pactline Native Signature (isEmeisSignature) — /api/v1/nativesigning. Enums
// travel as their backend int values (Emeis.Contracting.Domain.NativeSigning).

export const NATIVE_SIGNING_PROVIDER = "LawSignaNative";

export const NativeSigningRequestStatus = {
  InProgress: 1,
  Completed: 2,
  Declined: 3,
  ChangesRequested: 4,
  Cancelled: 5,
} as const;

export const NativeSigningSignerStatus = {
  Waiting: 1,
  Pending: 2,
  Viewed: 3,
  Signed: 4,
  Declined: 5,
  ChangesRequested: 6,
  Cancelled: 7,
} as const;

export const NativeSigningOrder = { Sequential: 1, Parallel: 2 } as const;

export const NativeSigningFieldType = {
  Signature: 1,
  Initials: 2,
  Date: 3,
  Name: 4,
  Designation: 5,
  Text: 6,
  Checkbox: 7,
  Radio: 8,
} as const;
export type NativeSigningFieldTypeValue =
  (typeof NativeSigningFieldType)[keyof typeof NativeSigningFieldType];

export const NativeSignatureMethod = {
  Drawn: 1,
  Uploaded: 2,
  Typed: 3,
  Saved: 4,
} as const;
export type NativeSignatureMethodValue =
  (typeof NativeSignatureMethod)[keyof typeof NativeSignatureMethod];

export const NativeSigningAuthLevel = {
  LoggedInUser: 1,
  EmailVerification: 2,
  EmailOtp: 3,
  Enhanced: 4,
} as const;

export interface NativeSigningConfig {
  defaultProvider: string | null;
  enableNativeSigning: boolean;
  allowDraw: boolean;
  allowUpload: boolean;
  allowType: boolean;
  allowSaved: boolean;
  requireAuthenticatedUser: boolean;
  requireEmailOtp: boolean;
  requireSmsOtp: boolean;
  maintainAuditTrail: boolean;
  generateCertificate: boolean;
  attachCertificateToDocument: boolean;
  generateDocumentHash: boolean;
  appearanceShowName: boolean;
  appearanceShowDate: boolean;
  appearanceShowTime: boolean;
  appearanceShowTransactionId: boolean;
  legalNotice: string | null;
  /** NativeSigningAuthLevel values an authenticator exists for. */
  availableAuthenticationLevels: number[];
  canEdit: boolean;
  version: number;
}

/** A field the initiator places before sending (fractions of the page, 0..1). */
export interface NativeSigningFieldSpec {
  signerEmail: string;
  fieldType: NativeSigningFieldTypeValue;
  label?: string;
  required: boolean;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  groupName?: string;
}

export interface NativeSigningSendOptions {
  signingOrder: 1 | 2;
  message?: string;
  fields: NativeSigningFieldSpec[];
}

export interface NativeSigningSigner {
  id: string;
  memberId: string;
  name: string;
  email: string;
  sequence: number;
  status: number;
  signatureTransactionId: string | null;
  signatureMethod: number | null;
  notifiedAt: string | null;
  firstViewedAt: string | null;
  signedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  isCurrentUser: boolean;
}

export interface NativeSigningField {
  id: string;
  signerId: string;
  fieldType: NativeSigningFieldTypeValue;
  label: string | null;
  required: boolean;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  groupName: string | null;
  value: string | null;
  hasImage: boolean;
  completedAt: string | null;
  isMine: boolean;
}

export interface NativeSigningRequest {
  id: string;
  contractId: string;
  contractNumber: string | null;
  contractTitle: string | null;
  transactionId: string;
  status: number;
  signingOrder: number;
  currentSequence: number;
  authenticationLevel: number;
  subject: string | null;
  message: string | null;
  initiatorId: string;
  initiatorName: string | null;
  docxFileName: string | null;
  documentVersionNumber: string | null;
  originalDocumentHash: string | null;
  signedDocumentHash: string | null;
  hasSignedDocument: boolean;
  hasCertificate: boolean;
  pageCount: number;
  sentAt: string;
  completedAt: string | null;
  closedAt: string | null;
  closeReason: string | null;
  signers: NativeSigningSigner[];
  fields: NativeSigningField[];
  myRole: "Signer" | "Initiator" | "Admin" | "Viewer";
  mySignerId: string | null;
  canSign: boolean;
  canCancel: boolean;
  allowedMethods: number[];
  requiredAuthenticationLevels: number[];
  legalNotice: string | null;
  consentText: string;
}

export interface NativeSigningRequestSummary {
  id: string;
  contractId: string;
  contractNumber: string | null;
  contractTitle: string | null;
  transactionId: string;
  status: number;
  initiatorName: string | null;
  sentAt: string;
  mySignerStatus: number;
  pendingFieldCount: number;
  mySignedAt: string | null;
  mySignatureTransactionId: string | null;
}

export interface NativeSigningMySummary {
  pendingSignatures: number;
  waitingForTurn: number;
  signedByMe: number;
}

export interface NativeSigningEvent {
  id: string;
  signerId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  signatureMethod: number | null;
  authenticationLevel: number | null;
  occurredAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  device: string | null;
  documentVersionNumber: string | null;
  transactionId: string | null;
  signingOrder: number | null;
  fieldIds: string | null;
  documentHash: string | null;
  details: string | null;
}

export interface NativeSigningFieldValue {
  fieldId: string;
  value?: string;
  /** PNG data URL for a signature/initials field. */
  imageDataUrl?: string;
  useSaved?: boolean;
}

export interface NativeSigningSaveSignature {
  kind: 1 | 2;
  method: NativeSignatureMethodValue;
  imageDataUrl?: string;
  consent: boolean;
}

export interface NativeSigningSignPayload {
  fields: NativeSigningFieldValue[];
  signatureMethod: NativeSignatureMethodValue;
  consent: boolean;
  saveSignature?: NativeSigningSaveSignature;
  authentication?: Record<string, string>;
}

export interface MemberSignature {
  kind: number;
  method: number;
  consentedAt: string;
  lastUsedAt: string | null;
}

const blob = async (url: string, body?: unknown): Promise<Blob> => {
  const res = body === undefined
    ? await api.get(url, { responseType: "blob" })
    : await api.post(url, body, { responseType: "blob" });
  return res.data as Blob;
};

export const nativeSigningApi = {
  getConfig: async (): Promise<NativeSigningConfig> =>
    (await api.get("/nativesigning/config")).data,
  updateConfig: async (
    config: Omit<NativeSigningConfig, "availableAuthenticationLevels" | "canEdit" | "version">,
  ): Promise<NativeSigningConfig> =>
    (await api.post("/nativesigning/config", config)).data,

  /** The final DOCX rendered to PDF, for placing fields before sending. */
  preview: (contractId: string, docxS3Key: string): Promise<Blob> =>
    blob("/nativesigning/preview", { contractId, docxS3Key }),

  getForContract: async (contractId: string): Promise<NativeSigningRequest | null> => {
    const res = await api.get(`/nativesigning/contract/${contractId}`);
    return res.data && typeof res.data === "object" && "id" in res.data ? res.data : null;
  },
  /** scope: "pending" (default) waits for my signature, "signed" what I signed, "all" both. */
  getMyRequests: async (scope: "pending" | "signed" | "all" = "pending"): Promise<NativeSigningRequestSummary[]> =>
    (await api.get("/nativesigning/my-requests", { params: { scope } })).data ?? [],
  getMySummary: async (): Promise<NativeSigningMySummary> =>
    (await api.get("/nativesigning/my-summary")).data,
  getRequest: async (requestId: string): Promise<NativeSigningRequest> =>
    (await api.get(`/nativesigning/request/${requestId}`)).data,
  getAudit: async (requestId: string): Promise<NativeSigningEvent[]> =>
    (await api.get(`/nativesigning/request/${requestId}/audit`)).data ?? [],
  getDocument: (requestId: string): Promise<Blob> =>
    blob(`/nativesigning/request/${requestId}/document`),
  getSignedDocument: (requestId: string): Promise<Blob> =>
    blob(`/nativesigning/request/${requestId}/signed-document`),
  getCertificate: (requestId: string): Promise<Blob> =>
    blob(`/nativesigning/request/${requestId}/certificate`),
  getFieldImage: (requestId: string, fieldId: string): Promise<Blob> =>
    blob(`/nativesigning/request/${requestId}/field/${fieldId}/image`),

  /** Sign Document: the one call that executes the electronic signature. */
  sign: async (requestId: string, payload: NativeSigningSignPayload): Promise<NativeSigningRequest> =>
    (await api.post(`/nativesigning/request/${requestId}/sign`, payload)).data,
  decline: async (requestId: string, reason: string): Promise<NativeSigningRequest> =>
    (await api.post(`/nativesigning/request/${requestId}/decline`, { reason })).data,
  requestChanges: async (requestId: string, reason: string): Promise<NativeSigningRequest> =>
    (await api.post(`/nativesigning/request/${requestId}/request-changes`, { reason })).data,
  cancel: async (requestId: string, reason: string): Promise<NativeSigningRequest> =>
    (await api.post(`/nativesigning/request/${requestId}/cancel`, { reason })).data,

  getMySignatures: async (): Promise<MemberSignature[]> =>
    (await api.get("/nativesigning/signatures/mine")).data ?? [],
  getMySignatureImage: (kind: 1 | 2): Promise<Blob> =>
    blob(`/nativesigning/signatures/mine/${kind}/image`),
  /** Save Signature: keeps the image for future signing; signs nothing. */
  saveMySignature: async (payload: NativeSigningSaveSignature): Promise<MemberSignature> =>
    (await api.post("/nativesigning/signatures/mine", payload)).data,
  deleteMySignature: async (kind: 1 | 2): Promise<void> => {
    await api.delete(`/nativesigning/signatures/mine/${kind}`);
  },
};

export const SIGNING_REQUEST_STATUS_LABEL: Record<number, string> = {
  1: "In progress",
  2: "Completed",
  3: "Declined",
  4: "Changes requested",
  5: "Withdrawn",
};

export const SIGNER_STATUS_LABEL: Record<number, string> = {
  1: "Waiting for their turn",
  2: "Pending",
  3: "Viewed",
  4: "Signed",
  5: "Declined",
  6: "Changes requested",
  7: "Cancelled",
};

export const SIGNATURE_METHOD_LABEL: Record<number, string> = {
  1: "Drawn Signature",
  2: "Uploaded Signature",
  3: "Typed Signature",
  4: "Saved Signature",
};

export const FIELD_TYPE_LABEL: Record<number, string> = {
  1: "Signature",
  2: "Initials",
  3: "Date",
  4: "Name",
  5: "Designation",
  6: "Text",
  7: "Checkbox",
  8: "Radio button",
};
